// Conduit (M18.1). PURE (ENG-1): the prismarine-frame check that powers a conduit
// and the range/effects it projects. A conduit sits in water; a frame of prismarine
// blocks around it (the borders of the three 5×5 squares centred on it in the xy,
// xz and yz planes — up to 42 blocks) activates it. The more frame blocks, the
// wider the Conduit Power area; a fully framed conduit also zaps a nearby hostile.
import { CONDUIT } from '../config';

/** Blocks that count toward a conduit frame (MC 1.21). */
export const CONDUIT_FRAME_BLOCKS: readonly string[] = [
  'prismarine', 'prismarine_bricks', 'dark_prismarine', 'sea_lantern',
];
export function isConduitFrame(block: string | null): boolean {
  return block !== null && CONDUIT_FRAME_BLOCKS.includes(block);
}

/**
 * The 42 frame offsets: the union of the borders (Chebyshev distance 2) of the
 * three 5×5 squares centred on the conduit in the xy, xz and yz planes. Built once.
 */
export const CONDUIT_FRAME_OFFSETS: ReadonlyArray<readonly [number, number, number]> = (() => {
  const seen = new Set<string>();
  const out: Array<[number, number, number]> = [];
  const add = (dx: number, dy: number, dz: number): void => {
    const k = `${dx},${dy},${dz}`;
    if (!seen.has(k)) { seen.add(k); out.push([dx, dy, dz]); }
  };
  for (let a = -2; a <= 2; a++) for (let b = -2; b <= 2; b++) {
    if (Math.max(Math.abs(a), Math.abs(b)) !== 2) continue; // border ring only
    add(a, b, 0); // xy plane
    add(a, 0, b); // xz plane
    add(0, a, b); // yz plane
  }
  return out;
})();

/** Count prismarine frame blocks around a conduit. `at(dx,dy,dz)` reads a neighbour. */
export function conduitFrameCount(at: (dx: number, dy: number, dz: number) => string | null): number {
  let n = 0;
  for (const [dx, dy, dz] of CONDUIT_FRAME_OFFSETS) if (isConduitFrame(at(dx, dy, dz))) n++;
  return n;
}

/** The conduit's inner 3×3×3 core must be water (or air pocket) for it to power up. */
export function conduitCoreClear(at: (dx: number, dy: number, dz: number) => string | null): boolean {
  for (let dx = -1; dx <= 1; dx++) for (let dy = -1; dy <= 1; dy++) for (let dz = -1; dz <= 1; dz++) {
    if (dx === 0 && dy === 0 && dz === 0) continue; // the conduit itself
    const b = at(dx, dy, dz);
    if (b !== null && b !== 'water' && b !== 'air') return false;
  }
  return true;
}

/** A conduit activates once its frame reaches the minimum block count. */
export function conduitActive(frameCount: number): boolean {
  return frameCount >= CONDUIT.MIN_FRAME;
}

/** Block radius of the Conduit Power area (MC: ⌊frame/7⌋·16), 0 when inactive. */
export function conduitRange(frameCount: number): number {
  if (!conduitActive(frameCount)) return 0;
  return Math.min(CONDUIT.MAX_RANGE, Math.floor(frameCount / 7) * CONDUIT.RANGE_STEP);
}

/** Whether a player at (dx,dy,dz) from the conduit is inside its Conduit Power area. */
export function conduitInRange(frameCount: number, dx: number, dy: number, dz: number): boolean {
  const r = conduitRange(frameCount);
  return r > 0 && Math.abs(dx) <= r && Math.abs(dy) <= r && Math.abs(dz) <= r;
}

/** A fully framed conduit (≥ ATTACK_FRAME) zaps a hostile within ATTACK_RADIUS. */
export function conduitAttacks(frameCount: number): boolean {
  return frameCount >= CONDUIT.ATTACK_FRAME;
}
